import { fmtINR } from '../../lib/currency';
import { useCallback, useEffect, useState } from 'react';
import {
  planningApi,
  type ActivityEntry,
  type ExtensionPoint,
  type PlanningApproval,
  type PlanningAssumption,
  type PlanningBaseline,
  type PlanningCalendar,
  type PlanningCycle,
  type PlanningDashboard,
  type PlanningKPI,
  type PlanningModel,
  type PlanningScenario,
  type PlanningTask,
  type PlanningVersion,
  type PlanningWorkspace,
  type ScenarioComparison,
} from '../../api/planning';

type Tab = 'overview' | 'cycles' | 'versions' | 'scenarios' | 'approvals' | 'tasks' | 'modules' | 'activity';

function statusClass(status: string) {
  if (status === 'published' || status === 'approved') return 'text-emerald-700 bg-emerald-50';
  if (status === 'pending') return 'text-amber-700 bg-amber-50';
  if (status === 'draft') return 'text-slate-600 bg-slate-50';
  if (status === 'rejected' || status === 'cancelled') return 'text-red-700 bg-red-50';
  return 'text-blue-700 bg-blue-50';
}


export function PlanningPlatformPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<PlanningDashboard | null>(null);
  const [workspace, setWorkspace] = useState<PlanningWorkspace | null>(null);
  const [cycles, setCycles] = useState<PlanningCycle[]>([]);
  const [versions, setVersions] = useState<PlanningVersion[]>([]);
  const [scenarios, setScenarios] = useState<PlanningScenario[]>([]);
  const [comparison, setComparison] = useState<ScenarioComparison | null>(null);
  const [assumptions, setAssumptions] = useState<PlanningAssumption[]>([]);
  const [kpis, setKpis] = useState<PlanningKPI[]>([]);
  const [tasks, setTasks] = useState<PlanningTask[]>([]);
  const [approvals, setApprovals] = useState<PlanningApproval[]>([]);
  const [baselines, setBaselines] = useState<PlanningBaseline[]>([]);
  const [calendar, setCalendar] = useState<PlanningCalendar | null>(null);
  const [modules, setModules] = useState<PlanningModel[]>([]);
  const [extensions, setExtensions] = useState<ExtensionPoint[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, ws, cyc, ver, sc, appr, tsk, bl, cal, mod, ext, act] = await Promise.all([
        planningApi.dashboard(),
        planningApi.getWorkspace(),
        planningApi.listCycles(),
        planningApi.listVersions(),
        planningApi.listScenarios(),
        planningApi.listApprovals(),
        planningApi.listTasks(),
        planningApi.listBaselines(),
        planningApi.getCalendar().catch(() => null),
        planningApi.listModules(),
        planningApi.extensionPoints(),
        planningApi.listActivity(),
      ]);
      setDashboard(dash);
      setWorkspace(ws);
      setCycles(cyc);
      setVersions(ver);
      setScenarios(sc);
      setApprovals(appr);
      setTasks(tsk);
      setBaselines(bl);
      setCalendar(cal);
      setModules(mod);
      setExtensions(ext);
      setActivity(act);

      const published = ver.find((v) => v.status === 'published');
      if (published) {
        const [asm, kpiList, cmp] = await Promise.all([
          planningApi.listAssumptions(published.id),
          planningApi.listKPIs(published.id),
          planningApi.compareScenarios(published.id),
        ]);
        setAssumptions(asm);
        setKpis(kpiList);
        setComparison(cmp);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load planning platform');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleApprove = async (id: string) => {
    setBusy(true);
    try {
      await planningApi.decideApproval(id, true);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approval failed');
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'cycles', label: 'Cycles' },
    { id: 'versions', label: 'Versions' },
    { id: 'scenarios', label: 'Scenarios' },
    { id: 'approvals', label: 'Approvals' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'modules', label: 'Modules' },
    { id: 'activity', label: 'Activity' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Enterprise Planning Platform</h1>
            <p className="mt-1 text-sm text-slate-600">
              Unified planning workspace — cycles, versions, scenarios, approvals, and cross-domain coordination
            </p>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={busy}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
        {workspace && (
          <p className="mt-2 text-sm text-slate-500">
            Workspace: <span className="font-medium text-slate-700">{workspace.name}</span>
            {' · '}FY{workspace.fiscal_year}
            {calendar && ` · ${calendar.name} (${calendar.period_count} periods)`}
          </p>
        )}
      </div>

      {error && (
        <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="border-b border-slate-200 bg-white px-6">
        <nav className="flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {tab === 'overview' && dashboard && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
              {[
                { label: 'Active Cycles', value: dashboard.active_cycles },
                { label: 'Active Versions', value: dashboard.active_versions },
                { label: 'Pending Approvals', value: dashboard.pending_approvals },
                { label: 'Open Tasks', value: dashboard.open_tasks },
                { label: 'Scenarios', value: dashboard.active_scenarios },
                { label: 'Modules', value: dashboard.registered_modules },
                { label: 'Baselines', value: dashboard.baseline_count },
              ].map((kpi) => (
                <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
                </div>
              ))}
            </div>

            {comparison && comparison.scenarios.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Scenario Comparison — S&OP</h2>
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-500">
                        <th className="pb-2 pr-4">Scenario</th>
                        <th className="pb-2 pr-4">Revenue Target</th>
                        <th className="pb-2 pr-4">Gross Margin</th>
                        <th className="pb-2">Inv. Turns</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.scenarios.map((sc) => {
                        const scKpis = comparison.kpis_by_scenario[sc.id] ?? [];
                        const revenue = scKpis.find((k) => k.metric_key === 'revenue');
                        const margin = scKpis.find((k) => k.metric_key === 'gross_margin_pct');
                        const turns = scKpis.find((k) => k.metric_key === 'inventory_turns');
                        return (
                          <tr key={sc.id} className="border-b border-slate-100">
                            <td className="py-2 pr-4 font-medium text-slate-800">{sc.name}</td>
                            <td className="py-2 pr-4">{revenue ? fmtINR(revenue.target) : '—'}</td>
                            <td className="py-2 pr-4">{margin ? `${margin.target}%` : '—'}</td>
                            <td className="py-2">{turns ? `${turns.target}x` : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {assumptions.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Planning Assumptions</h2>
                <ul className="mt-4 space-y-3">
                  {assumptions.map((a) => (
                    <li key={a.id} className="flex items-start justify-between rounded-lg bg-slate-50 px-4 py-3">
                      <div>
                        <span className="text-xs font-medium uppercase text-slate-500">{a.category}</span>
                        <p className="text-sm text-slate-800">{a.statement}</p>
                        {a.value && <p className="text-sm font-medium text-indigo-600">{a.value}</p>}
                      </div>
                      <span className="text-xs text-slate-500">{Math.round(a.confidence * 100)}% conf.</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {tab === 'cycles' && (
          <div className="space-y-3">
            {cycles.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-slate-900">{c.name}</h3>
                    <p className="text-sm text-slate-500">
                      {c.domain} · {c.horizon} · {c.version_count} version(s)
                    </p>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass(c.status)}`}>
                    {c.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'versions' && (
          <div className="space-y-3">
            {versions.map((v) => (
              <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-slate-900">
                      {v.name} <span className="text-slate-400">v{v.version}</span>
                    </h3>
                    <p className="text-sm text-slate-500">
                      {v.domain} · {v.horizon}
                      {v.is_baseline && ' · baseline'}
                    </p>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass(v.status)}`}>
                    {v.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'scenarios' && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {scenarios.map((sc) => (
              <div key={sc.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="font-medium text-slate-900">{sc.name}</h3>
                <p className="mt-1 text-sm text-slate-500">{sc.type.replace('_', ' ')}</p>
                <p className="mt-2 text-xs text-slate-400">{sc.kpi_count} KPIs</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'approvals' && (
          <div className="space-y-3">
            {approvals.length === 0 && (
              <p className="text-sm text-slate-500">No pending approvals.</p>
            )}
            {approvals.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div>
                  <p className="font-medium text-slate-900">Version {a.version_id.slice(0, 8)}…</p>
                  <p className="text-sm text-slate-500">Requested {new Date(a.requested_at).toLocaleDateString()}</p>
                </div>
                {a.status === 'pending' ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleApprove(a.id)}
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                ) : (
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass(a.status)}`}>
                    {a.status}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === 'tasks' && (
          <div className="space-y-3">
            {tasks.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-slate-900">{t.title}</h3>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass(t.status)}`}>
                    {t.status}
                  </span>
                </div>
                {t.due_date && (
                  <p className="mt-1 text-sm text-slate-500">Due {new Date(t.due_date).toLocaleDateString()}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === 'modules' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Registered Modules</h2>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                {modules.map((m) => (
                  <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h3 className="font-medium text-slate-900">{m.name}</h3>
                    <p className="text-sm text-slate-500">{m.domain}</p>
                    <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs ${m.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {m.enabled ? 'enabled' : 'pending'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Extension Points</h2>
              <p className="text-sm text-slate-500">Future planning modules plug in here without modifying the foundation.</p>
              <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {extensions.map((ep) => (
                  <div key={ep.key} className="rounded-lg border border-dashed border-indigo-200 bg-indigo-50/50 p-3">
                    <h4 className="text-sm font-medium text-indigo-900">{ep.name}</h4>
                    <p className="mt-1 text-xs text-indigo-700">{ep.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'activity' && (
          <div className="space-y-2">
            {activity.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
                <span className="font-medium text-slate-700">{a.action}</span>
                <span className="text-slate-500"> — {a.message}</span>
                <span className="ml-2 text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
